import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { LaurelMark } from "@/components/Logo";
import { ALL_QUESTIONS, SCALE, computeResults } from "@/data/auditData";

export default function Audit() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState(() => Array(ALL_QUESTIONS.length).fill(null));

  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) sessionStorage.setItem("lov_ref", ref);
  }, [searchParams]);

  const q = ALL_QUESTIONS[index];
  const total = ALL_QUESTIONS.length;
  const progress = Math.round(((index + (answers[index] ? 1 : 0)) / total) * 100);
  const isLast = index === total - 1;
  const areaChanged = index > 0 && ALL_QUESTIONS[index - 1].areaKey !== q.areaKey;

  const answer = (value) => {
    const next = [...answers];
    next[index] = value;
    setAnswers(next);
    setTimeout(() => {
      if (isLast) {
        const results = computeResults(next);
        sessionStorage.setItem("lov_audit_results", JSON.stringify({ answers: next, results }));
        navigate("/results");
      } else {
        setIndex(index + 1);
      }
    }, 320);
  };

  return (
    <div className="min-h-screen bg-burgundy-deep text-champagne flex flex-col" data-testid="audit-page">
      <div className="max-w-3xl w-full mx-auto px-6 pt-8 flex items-center justify-between">
        <button
          data-testid="audit-back-btn"
          onClick={() => (index === 0 ? navigate("/") : setIndex(index - 1))}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-champagne/60 hover:text-champagne transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> {index === 0 ? "Exit" : "Previous"}
        </button>
        <LaurelMark className="w-6 h-6" chip />
        <span className="text-xs uppercase tracking-[0.25em] text-champagne/60" data-testid="audit-progress-label">
          {index + 1} of {total}
        </span>
      </div>

      <div className="max-w-3xl w-full mx-auto px-6 mt-6">
        <div className="h-px bg-champagne/15 relative overflow-hidden rounded-full">
          <motion.div
            className="absolute inset-y-0 left-0 bg-gold"
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            data-testid="audit-progress-bar"
          />
        </div>
      </div>

      <div className="flex-1 flex items-center">
        <div className="max-w-3xl w-full mx-auto px-6 py-16">
          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            >
              {(index === 0 || areaChanged) && (
                <p className="text-xs uppercase tracking-[0.35em] text-gold mb-6" data-testid="audit-area-label">
                  Area · {q.area}
                </p>
              )}
              <h2
                className="font-serif text-2xl sm:text-3xl lg:text-4xl leading-snug text-champagne"
                data-testid="audit-question-text"
              >
                “{q.text}”
              </h2>
              <p className="mt-4 text-sm text-champagne/50">
                How true is this of you, right now — not on your best day?
              </p>

              <div className="mt-12 grid gap-3">
                {SCALE.map((s) => {
                  const selected = answers[index] === s.value;
                  return (
                    <button
                      key={s.value}
                      data-testid={`audit-answer-${s.value}`}
                      onClick={() => answer(s.value)}
                      className={`group flex items-center justify-between border px-6 py-4 text-left transition-colors duration-200 ${
                        selected
                          ? "border-gold bg-gold/15"
                          : "border-champagne/20 hover:border-gold/60 hover:bg-champagne/5"
                      }`}
                    >
                      <span className="flex items-center gap-4">
                        <span
                          className={`w-8 h-8 rounded-full border flex items-center justify-center font-serif text-lg transition-colors ${
                            selected ? "border-gold text-gold" : "border-champagne/30 text-champagne/70 group-hover:border-gold/60"
                          }`}
                        >
                          {s.value}
                        </span>
                        <span className="text-sm sm:text-base tracking-wide">{s.label}</span>
                      </span>
                      <ArrowRight
                        className={`w-4 h-4 transition-all ${
                          selected ? "text-gold opacity-100" : "opacity-0 group-hover:opacity-60"
                        }`}
                      />
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
